import { Avatar, Button, Layout, Menu, Typography } from "antd";
import styled from "styled-components";

const { Text } = Typography;
const { Header } = Layout;

/**
 * HEADER — Frosted glass with subtle border.
 * backdrop-filter: blur creates the glassmorphism effect.
 * Falls back to solid white on browsers without support.
 */
/**
 * HEADER — Override Ant Design's default header height/padding/line-height
 * to ensure content is perfectly vertically centered.
 * The key fix: explicit height + line-height: normal (not Ant's default 64px line-height).
 */
export const StyledHeader = styled(Header)`
  &.ant-layout-header {
    top: 0;
    height: var(--header-height);
    line-height: normal;
    padding: 0 var(--space-4);
    background: var(--glass-bg);
    backdrop-filter: var(--glass-blur);
    -webkit-backdrop-filter: var(--glass-blur);
    position: sticky;
    z-index: var(--z-modal);
    box-shadow: var(--shadow-xs);
    border-bottom: 1px solid var(--color-border);
    display: flex;
    align-items: center;

    @supports not (backdrop-filter: blur(1px)) {
      background: var(--color-bg-elevated);
    }
  }
`;

export const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: var(--content-max-width);
  margin: 0 auto;
  gap: var(--space-2);
`;

/* ── Logo ──────────────────────────────────────────────────────── */

export const LogoCol = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  cursor: pointer;
  line-height: 40px;
  transition: opacity var(--duration-fast) var(--ease-out);

  &:hover {
    opacity: 0.85;
  }
`;

export const HomeLink = styled.button`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  border: none;
  background: var(--color-primary-light);
  cursor: pointer;
  color: var(--color-primary);
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
  height: 36px;
  padding: 0 14px;
  border-radius: var(--radius-md);
  transition: var(--transition-all);

  .anticon {
    font-size: 16px;
  }

  &:hover {
    background: var(--color-primary-lighter);
  }

  @media (max-width: 767px) {
    height: 32px;
    font-size: var(--text-xs);
  }
`;

export const LogoIcon = styled.div`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

/**
 * Brand text uses gradient fill — matches the primary brand identity.
 * Display font for brand presence.
 */
export const BrandText = styled.span`
  background: var(--color-gradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-family: var(--font-display);
  font-size: 1.1em;
  font-weight: var(--weight-bold);
  white-space: nowrap;
  display: flex;
  align-items: center;

  @media (max-width: 480px) {
    display: none;
  }
`;

/* ── Actions (right side) ──────────────────────────────────────── */

export const ActionsCol = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  min-width: 0;
`;

/* ── Language switcher ─────────────────────────────────────────── */
/**
 * Pill-shaped toggle with gradient active state.
 * Compact, doesn't fight for attention with auth buttons.
 */
export const LangSwitch = styled.div`
  display: none;
  border-radius: var(--radius-full);
  overflow: hidden;
  background: var(--color-bg-muted);
  padding: 2px;
  flex-shrink: 0;

  @media (min-width: 768px) {
    display: flex;
  }
`;

/* Drawer-specific language switch — always visible, centered */
export const DrawerLangSwitch = styled.div`
  display: flex;
  border-radius: var(--radius-full);
  overflow: hidden;
  background: var(--color-bg-muted);
  padding: 2px;
  flex-shrink: 0;
`;

export const LangOption = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border: none;
  border-radius: var(--radius-full);
  cursor: ${({ $active }) => ($active ? "default" : "pointer")};
  background: ${({ $active }) =>
    $active ? "var(--color-gradient)" : "transparent"};
  color: ${({ $active }) =>
    $active ? "var(--color-text-on-primary)" : "var(--color-text-tertiary)"};
  font-weight: ${({ $active }) =>
    $active ? "var(--weight-semibold)" : "var(--weight-normal)"};
  font-size: var(--text-xs);
  transition: var(--transition-all);
  box-shadow: ${({ $active }) =>
    $active ? "var(--shadow-primary)" : "none"};

  &:hover {
    color: ${({ $active }) =>
      $active ? "var(--color-text-on-primary)" : "var(--color-primary)"};
  }
`;

/* ── Auth buttons ──────────────────────────────────────────────── */
/**
 * Sign In = ghost/text button — low visual weight, login is optional.
 * Get Started = filled primary CTA — draws attention to registration.
 * This hierarchy makes the app feel welcoming, not gate-kept.
 */
export const LoginBtn = styled(Button)`
  &.ant-btn {
    border: 1.5px solid var(--color-primary);
    background: transparent;
    color: var(--color-primary);
    border-radius: var(--radius-md);
    font-weight: var(--weight-semibold);
    font-size: var(--text-sm);
    padding: 4px 16px;
    height: 36px;
    box-shadow: none;
    transition: var(--transition-colors);

    &:hover {
      color: var(--color-primary-hover) !important;
      border-color: var(--color-primary-hover) !important;
      background: var(--color-primary-light) !important;
    }

    @media (max-width: 767px) {
      height: 36px;
      padding: 4px 14px;
      font-size: 13px;
    }
  }
`;

export const RegisterBtn = styled(Button)`
  &.ant-btn {
    background: var(--color-gradient);
    border: none;
    border-radius: var(--radius-md);
    font-weight: var(--weight-semibold);
    font-size: var(--text-sm);
    height: 36px;
    box-shadow: var(--shadow-primary);
    transition: var(--transition-all);

    &:hover,
    &:focus {
      background: var(--color-gradient-hover) !important;
      border: none !important;
      box-shadow: var(--shadow-primary-lg);
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0);
    }

    > .ant-wave {
      display: none;
    }

    @media (max-width: 767px) {
      height: 36px;
      padding: 4px 14px;
      font-size: 13px;
    }
  }
`;

/* ── User avatar (logged in state) ─────────────────────────────── */
export const UserAvatar = styled(Avatar)`
  background: var(--color-gradient);
  cursor: pointer;
  box-shadow: var(--shadow-primary);
  transition: var(--transition-all);

  &:hover {
    box-shadow: var(--shadow-primary-lg);
    transform: scale(1.05);
  }
`;

/* ── Drawer (mobile menu / profile menu) ───────────────────────── */
export const DrawerTitle = styled.span`
  color: var(--color-primary);
  font-family: var(--font-display);
  font-size: 1em;
  font-weight: var(--weight-semibold);
`;

export const StyledMenu = styled(Menu)`
  background: transparent;
  border-right: none;
  color: var(--color-text);

  .ant-menu-item {
    color: var(--color-text) !important;
    padding: 12px 20px !important;
    height: 52px !important;
    line-height: 28px !important;
    display: flex;
    align-items: center;
    border-radius: var(--radius-md) !important;
    margin: 4px 8px !important;
    width: calc(100% - 16px) !important;
    transition: var(--transition-all) !important;
    font-weight: var(--weight-medium);
  }

  .ant-menu-item:hover {
    background-color: var(--color-primary-light) !important;
    color: var(--color-primary) !important;
  }

  .ant-menu-item-selected {
    background-color: var(--color-primary-light) !important;
    color: var(--color-primary) !important;
    font-weight: var(--weight-semibold) !important;
  }

  .ant-menu-item-selected .anticon {
    color: var(--color-primary) !important;
  }
`;

/* ── Bell / Announcements ──────────────────────────────────────── */

export const BellBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  cursor: pointer;
  width: 38px;
  height: 38px;
  padding: 0;
  border: 1.5px solid var(--color-primary);
  border-radius: var(--radius-full);
  color: var(--color-primary);
  flex-shrink: 0;
  transition: var(--transition-colors);

  /* antd Badge ima svoju boju teksta, pa ikonica ne nasleđuje ružičastu. */
  .anticon {
    color: var(--color-primary);
  }

  &:hover {
    background: var(--color-primary-light);
  }
`;

export const BellDropdown = styled.div`
  width: 320px;
  max-height: 400px;
  display: flex;
  flex-direction: column;

  @media (max-width: 480px) {
    width: calc(100vw - 32px);
    max-width: 320px;
  }
`;

export const BellHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: var(--space-2);
  border-bottom: 1px solid var(--color-border-accent);
  margin-bottom: var(--space-2);
`;

export const BellTitle = styled(Text)`
  &.ant-typography {
    font-family: var(--font-display);
    font-size: var(--text-sm);
    font-weight: var(--weight-bold);
    color: var(--color-text);
  }
`;

export const BellSeeAll = styled.button`
  border: none;
  background: none;
  color: var(--color-primary);
  font-size: var(--text-xs);
  font-weight: var(--weight-semibold);
  cursor: pointer;
  padding: 0;
  transition: var(--transition-colors);

  &:hover {
    text-decoration: underline;
  }
`;

export const BellList = styled.div`
  overflow-y: auto;
  max-height: 340px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const BellItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  padding: var(--space-2);
  border-radius: var(--radius-sm);
  transition: background var(--duration-fast) var(--ease-out);

  &:hover {
    background: var(--color-bg-subtle);
  }
`;

export const BellItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
`;

export const BellItemLogo = styled.img`
  width: 36px;
  height: 36px;
  border-radius: var(--radius-sm);
  object-fit: contain;
  background: var(--color-primary-light);
  flex-shrink: 0;
`;

export const BellItemLogoFallback = styled.div`
  width: 36px;
  height: 36px;
  border-radius: var(--radius-sm);
  background: var(--color-primary-light);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--text-sm);
  font-weight: var(--weight-bold);
  color: var(--color-primary);
  flex-shrink: 0;
`;

export const BellItemText = styled.div`
  flex: 1;
  min-width: 0;
`;

export const BellItemTitle = styled(Text)`
  &.ant-typography {
    display: block;
    font-size: 13px;
    font-weight: var(--weight-semibold);
    color: var(--color-text);
    line-height: var(--leading-snug);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const BellItemTenant = styled(Text)`
  &.ant-typography {
    display: block;
    font-size: 11px;
    color: var(--color-text-tertiary);
    line-height: var(--leading-snug);
  }
`;

export const BellItemTime = styled(Text)`
  &.ant-typography {
    font-size: 10px;
    color: var(--color-text-tertiary);
    white-space: nowrap;
    flex-shrink: 0;
  }
`;

/* ── Responsive helpers ────────────────────────────────────────── */

export const MobileOnly = styled.div`
  display: flex;
  align-items: center;

  @media (min-width: 768px) {
    display: none;
  }
`;

export const BackBtn = styled(Button)`
  &.ant-btn {
    height: 38px;
    border-radius: var(--radius-md);
    background: var(--color-primary-light);
    border: 1px solid var(--color-primary-lighter);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 0 14px;
    font-size: var(--text-sm);
    font-weight: var(--weight-medium);
    color: var(--color-primary);
    transition: var(--transition-all);

    &:hover {
      background: var(--color-primary-lighter) !important;
      border-color: var(--color-primary) !important;
      color: var(--color-primary) !important;
      transform: translateX(-2px);
    }

    &:focus,
    &:focus-visible {
      outline: none !important;
      box-shadow: none !important;
      color: var(--color-primary) !important;
    }

    &:active {
      transform: translateX(0);
    }
  }
`;

export const DesktopOnly = styled.div`
  display: none;
  align-items: center;
  gap: var(--space-2);

  @media (min-width: 768px) {
    display: flex;
  }
`;

/* ── Theme toggle (sun/moon) ────────────────────────────────────── */
/**
 * THEME TOGGLE — Animated pill switch between light and dark mode.
 * Sun icon left, moon icon right. Active state slides a pill indicator.
 * Compact size fits naturally next to other header icons.
 */
export const ThemeToggle = styled.button<{ $isDark: boolean }>`
  position: relative;
  width: 56px;
  height: 28px;
  border-radius: var(--radius-full);
  border: 1px solid var(--color-border);
  background: var(--color-bg-muted);
  cursor: pointer;
  padding: 2px;
  flex-shrink: 0;
  transition: var(--transition-all);
  display: flex;
  align-items: center;

  &:hover {
    border-color: var(--color-primary);
  }

  /* Sliding pill indicator */
  &::after {
    content: "";
    position: absolute;
    top: 2px;
    left: ${({ $isDark }) => ($isDark ? "28px" : "2px")};
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: var(--color-gradient);
    transition: left var(--duration-normal) var(--ease-out);
    box-shadow: var(--shadow-sm);
  }
`;

export const ThemeToggleIcon = styled.span<{ $side: "left" | "right" }>`
  position: relative;
  z-index: 1;
  width: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  transition: color var(--duration-normal) var(--ease-out);
`;

/* ── Drawer: Header profile (replaces "Meni" title) ─────────────── */

export const DrawerHeaderProfile = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  cursor: pointer;
  flex: 1;
  min-width: 0;
  padding: 2px 0;
  transition: opacity var(--duration-fast) var(--ease-out);

  &:hover {
    opacity: 0.92;
  }
`;

export const DrawerHeaderAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: var(--radius-md);
  background: rgba(255, 255, 255, 0.22);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-on-primary);
  font-size: 16px;
  font-weight: var(--weight-bold);
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.18);
`;

export const DrawerHeaderInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const DrawerHeaderName = styled.div`
  font-size: var(--text-base);
  font-weight: var(--weight-semibold);
  color: var(--color-text-on-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
`;

export const DrawerHeaderEmail = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.75);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 2px;
`;

/* ── Drawer: Bottom action buttons ──────────────────────────────── */

export const DrawerBottomActions = styled.div`
  display: flex;
  gap: var(--space-2);
  padding: 0 var(--space-4);
`;

export const DrawerCloseAction = styled.button`
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 46px;
  padding: 0 var(--space-3);
  border: 1px solid var(--color-border);
  background: var(--color-bg-elevated);
  color: var(--color-text-secondary);
  border-radius: var(--radius-md);
  cursor: pointer;
  font-weight: var(--weight-semibold);
  font-size: var(--text-sm);
  transition: var(--transition-all);

  .anticon {
    font-size: 14px;
  }

  &:hover {
    border-color: var(--color-primary);
    color: var(--color-primary);
    background: var(--color-primary-light);
  }
`;

export const DrawerLogoutAction = styled.button`
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 46px;
  padding: 0 var(--space-3);
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: var(--color-error);
  border-radius: var(--radius-md);
  cursor: pointer;
  font-weight: var(--weight-semibold);
  font-size: var(--text-sm);
  transition: var(--transition-all);
  box-shadow: 0 1px 2px rgba(239, 68, 68, 0.08);

  .anticon {
    font-size: 14px;
  }

  &:hover {
    background: #fee2e2;
    border-color: var(--color-error);
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.18);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

/* ── Drawer: User card ──────────────────────────────────────────── */

export const DrawerUserCard = styled.div`
  margin: var(--space-4) var(--space-4) var(--space-3);
  padding: var(--space-4);
  background: var(--color-gradient);
  border-radius: var(--radius-lg);
  display: flex;
  align-items: center;
  gap: var(--space-3);
  box-shadow: var(--shadow-primary);
  cursor: pointer;
  transition: var(--transition-all);

  &:hover {
    box-shadow: var(--shadow-primary-lg);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const DrawerUserAvatar = styled.div`
  width: 44px;
  height: 44px;
  border-radius: var(--radius-md);
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-on-primary);
  font-size: 20px;
  flex-shrink: 0;
`;

export const DrawerUserInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const DrawerUserName = styled.div`
  font-size: var(--text-base);
  font-weight: var(--weight-semibold);
  color: var(--color-text-on-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const DrawerUserEmail = styled.div`
  font-size: var(--text-xs);
  color: rgba(255, 255, 255, 0.7);
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* ── Drawer: Upcoming booking card ──────────────────────────────── */

export const UpcomingBookingCard = styled.div`
  margin: 0 var(--space-4) var(--space-2);
  padding: var(--space-3) var(--space-4);
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border-accent);
  border-radius: var(--radius-lg);
  cursor: pointer;
  transition: var(--transition-all);
  box-shadow: var(--shadow-xs);

  &:hover {
    border-color: var(--color-primary);
    box-shadow: var(--shadow-sm);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const UpcomingBookingLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  font-size: 10px;
  font-weight: var(--weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: var(--color-primary);
  margin-bottom: var(--space-2);
`;

export const UpcomingBookingWhen = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  background: var(--color-primary-light);
  border-radius: var(--radius-full);
  font-size: 11px;
  font-weight: var(--weight-semibold);
  text-transform: none;
  letter-spacing: 0;
`;

export const UpcomingBookingTitle = styled.div`
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
  color: var(--color-text);
  line-height: var(--leading-snug);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const UpcomingBookingTenant = styled.div`
  font-size: var(--text-xs);
  color: var(--color-text-tertiary);
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const UpcomingBookingTime = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: var(--space-2);
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
  color: var(--color-text);

  .anticon {
    color: var(--color-primary);
    font-size: 13px;
  }
`;

export const UpcomingEmptyCard = styled.div`
  margin: 0 var(--space-4) var(--space-2);
  padding: var(--space-3) var(--space-4);
  background: var(--color-bg-elevated);
  border: 1px dashed var(--color-border-accent);
  border-radius: var(--radius-lg);
  display: flex;
  align-items: center;
  gap: var(--space-3);
`;

export const UpcomingEmptyIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: var(--radius-md);
  background: var(--color-primary-light);
  color: var(--color-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
`;

export const UpcomingEmptyText = styled.div`
  flex: 1;
  min-width: 0;
`;

export const UpcomingEmptyTitle = styled.div`
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
  color: var(--color-text);
`;

export const UpcomingEmptyHint = styled.div`
  font-size: var(--text-xs);
  color: var(--color-text-tertiary);
  margin-top: 2px;
`;

export const UpcomingEmptyCta = styled.button`
  border: none;
  background: var(--color-gradient);
  color: var(--color-text-on-primary);
  font-size: 12px;
  font-weight: var(--weight-semibold);
  padding: 6px 12px;
  border-radius: var(--radius-md);
  cursor: pointer;
  flex-shrink: 0;
  box-shadow: var(--shadow-primary);
  transition: var(--transition-all);

  &:hover {
    box-shadow: var(--shadow-primary-lg);
    transform: translateY(-1px);
  }
`;

export const UpcomingSkeleton = styled.div`
  margin: 0 var(--space-4) var(--space-2);
  padding: var(--space-3) var(--space-4);
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border-accent);
  border-radius: var(--radius-lg);
`;

/* ── Drawer: Quick shortcuts grid ───────────────────────────────── */

export const ShortcutsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-2);
  margin: 0 var(--space-4) var(--space-3);
`;

export const ShortcutTile = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: var(--space-3) var(--space-2);
  border: 1px solid var(--color-border-accent);
  background: var(--color-bg-elevated);
  border-radius: var(--radius-lg);
  cursor: pointer;
  transition: var(--transition-all);
  color: var(--color-text);
  font-size: var(--text-xs);
  font-weight: var(--weight-semibold);
  text-align: center;
  line-height: 1.2;
  box-shadow: var(--shadow-xs);

  &:hover {
    border-color: var(--color-primary);
    background: var(--color-primary-light);
    transform: translateY(-1px);
    box-shadow: var(--shadow-sm);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const ShortcutIcon = styled.div<{ $tint?: string }>`
  width: 36px;
  height: 36px;
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $tint }) => $tint || "var(--color-primary-light)"};
  color: var(--color-primary);
  font-size: 17px;

  ${ShortcutTile}:hover & {
    background: var(--color-gradient);
    color: var(--color-text-on-primary);
  }
`;

export const ShortcutBadge = styled.span`
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: var(--radius-full);
  background: var(--color-error);
  color: #fff;
  font-size: 10px;
  font-weight: var(--weight-bold);
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ShortcutIconWrap = styled.div`
  position: relative;
`;

/* ── Drawer: Section labels ────────────────────────────────────── */

export const DrawerSectionLabel = styled.div`
  font-size: 11px;
  font-weight: var(--weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: var(--color-text-tertiary);
  padding: var(--space-4) var(--space-5) var(--space-2);
`;

export const DrawerDivider = styled.div`
  height: 1px;
  background: var(--color-border-accent);
  margin: var(--space-2) var(--space-5);
`;

/* ── Drawer: Account menu item ─────────────────────────────────── */

/* iOS-style menu card: icon square (left) + label + chevron (right) */
export const DrawerAccountItem = styled.button<{ $tint?: string }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  width: calc(100% - var(--space-4));
  margin: 4px var(--space-2);
  padding: var(--space-3);
  border: 1px solid var(--color-border-accent);
  border-radius: var(--radius-lg);
  background: var(--color-bg-elevated);
  cursor: pointer;
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
  color: var(--color-text);
  text-align: left;
  transition: var(--transition-all);
  box-shadow: var(--shadow-xs);

  &:hover {
    border-color: var(--color-primary);
    box-shadow: var(--shadow-sm);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const DrawerAccountItemLeft = styled.span`
  display: inline-flex;
  align-items: center;
  gap: var(--space-3);
  min-width: 0;
  flex: 1;
`;

export const DrawerAccountItemIcon = styled.span<{ $tint?: string }>`
  width: 34px;
  height: 34px;
  border-radius: var(--radius-md);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${({ $tint }) => $tint || "var(--color-primary-light)"};
  color: var(--color-primary);
  font-size: 16px;
  transition: var(--transition-all);

  ${DrawerAccountItem}:hover & {
    background: var(--color-gradient);
    color: var(--color-text-on-primary);
  }
`;

export const DrawerAccountItemLabel = styled.span`
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
  color: var(--color-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const DrawerAccountItemChevron = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-tertiary);
  font-size: 11px;
  flex-shrink: 0;
  transition: var(--transition-all);

  ${DrawerAccountItem}:hover & {
    color: var(--color-primary);
    transform: translateX(3px);
  }
`;

/* ── Drawer: Bottom spacer ─────────────────────────────────────── */

export const DrawerBottom = styled.div`
  margin-top: auto;
  padding-bottom: max(var(--space-4), env(safe-area-inset-bottom));
`;

/* ── Drawer extras (mobile) ─────────────────────────────────────── */

export const DrawerSection = styled.div`
  padding: var(--space-4) var(--space-5);
  border-top: 1px solid var(--color-border-accent);
`;

export const DrawerRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-2) 0;
`;

export const DrawerSettingsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  width: calc(100% - var(--space-4));
  margin: 4px var(--space-2);
  padding: var(--space-3);
  border: 1px solid var(--color-border-accent);
  border-radius: var(--radius-lg);
  background: var(--color-bg-elevated);
  box-shadow: var(--shadow-xs);
  box-sizing: border-box;
`;

export const DrawerRowLabel = styled.span`
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
  color: var(--color-text-secondary);
`;

export const DrawerAuthButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-4) var(--space-5);
  border-top: 1px solid var(--color-border-accent);
`;

export const DrawerLoginBtn = styled(Button)`
  &.ant-btn {
    border-color: var(--color-primary);
    color: var(--color-primary);
    border-radius: var(--radius-md);
    font-weight: var(--weight-medium);
    height: 44px;
  }
`;

export const DrawerRegisterBtn = styled(Button)`
  &.ant-btn {
    background: var(--color-gradient);
    border: none;
    border-radius: var(--radius-md);
    font-weight: var(--weight-semibold);
    height: 44px;
    box-shadow: var(--shadow-primary);

    &:hover {
      background: var(--color-gradient-hover) !important;
    }

    > .ant-wave {
      display: none;
    }
  }
`;

export default {
  ActionsCol,
  BellBtn,
  BackBtn,
  MobileOnly,
  BellDropdown,
  BellHeader,
  BellItem,
  BellItemContent,
  BellItemLogo,
  BellItemLogoFallback,
  BellItemTenant,
  BellItemText,
  BellItemTime,
  BellItemTitle,
  BellList,
  BellSeeAll,
  BellTitle,
  BrandText,
  DesktopOnly,
  DrawerTitle,
  HeaderRow,
  HomeLink,
  LangOption,
  LangSwitch,
  LoginBtn,
  LogoCol,
  LogoIcon,
  RegisterBtn,
  StyledHeader,
  StyledMenu,
  ThemeToggle,
  ThemeToggleIcon,
  UserAvatar,
  DrawerLangSwitch,
  DrawerHeaderProfile,
  DrawerHeaderAvatar,
  DrawerHeaderInfo,
  DrawerHeaderName,
  DrawerHeaderEmail,
  DrawerBottomActions,
  DrawerCloseAction,
  DrawerLogoutAction,
  DrawerUserCard,
  DrawerUserAvatar,
  DrawerUserInfo,
  DrawerUserName,
  DrawerUserEmail,
  UpcomingBookingCard,
  UpcomingBookingLabel,
  UpcomingBookingWhen,
  UpcomingBookingTitle,
  UpcomingBookingTenant,
  UpcomingBookingTime,
  UpcomingEmptyCard,
  UpcomingEmptyIcon,
  UpcomingEmptyText,
  UpcomingEmptyTitle,
  UpcomingEmptyHint,
  UpcomingEmptyCta,
  UpcomingSkeleton,
  ShortcutsGrid,
  ShortcutTile,
  ShortcutIcon,
  ShortcutIconWrap,
  ShortcutBadge,
  DrawerSectionLabel,
  DrawerDivider,
  DrawerAccountItem,
  DrawerAccountItemLeft,
  DrawerAccountItemIcon,
  DrawerAccountItemLabel,
  DrawerAccountItemChevron,
  DrawerBottom,
  DrawerSection,
  DrawerRow,
  DrawerSettingsRow,
  DrawerRowLabel,
  DrawerAuthButtons,
  DrawerLoginBtn,
  DrawerRegisterBtn,
};
